import {Component, DestroyRef, HostListener, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '@environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {DsNavigationBarModule, DsNavigationItem} from '@bmw-ds/components';
import {ConfigurationLoader} from "../../core/configuration/services/configuration.loader";
import {SupportInformationService} from "../../core/support/support-information.service";
import {INavigationCategory} from "../../interfaces/INavigationCategory";
import {AppsService} from "../../core/apps/services/apps.service";
import {IIframeAppConfiguration} from "../../interfaces/IIframeAppConfiguration";
import {IWebComponentAppConfiguration} from "../../interfaces/IWebComponentAppConfiguration";
import {VehicleSearchService} from "../vehicle/vehicle-search-overlay/vehicle-search.service";

export const LOCAL_STORAGE_KEY_FOR_NAVIGATION_MENU_COLLAPSED = 'navigationMenuClosed';

@Component({
  selector: 'app-side-navigation',
  templateUrl: './side-navigation.component.html',
  styleUrls: ['./side-navigation.component.scss'],
  imports: [DsNavigationBarModule]
})
export class SideNavigationComponent implements OnInit {
  private readonly appsService: AppsService = inject(AppsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly configurationLoader: ConfigurationLoader = inject(ConfigurationLoader);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly vehicleSearchService: VehicleSearchService = inject(VehicleSearchService);
  supportInformationService: SupportInformationService = inject(SupportInformationService);

  expanded: boolean = true;
  showSidebar: boolean = false;
  navigationItems: DsNavigationItem[];

  constructor() {
    this.expanded = !window.localStorage.getItem(LOCAL_STORAGE_KEY_FOR_NAVIGATION_MENU_COLLAPSED);
  }

  ngOnInit(): void {
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
      // recreate menu on language change to update labels
      this.createAppEntries();
    });
    this.configurationLoader.configChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
      // wait for config change as some app visibilities depend on market config
      this.createAppEntries();
    });
  }

  onExpandedChange(): void {
    if (this.expanded) {
      window.localStorage.removeItem(LOCAL_STORAGE_KEY_FOR_NAVIGATION_MENU_COLLAPSED);
    } else {
      window.localStorage.setItem(LOCAL_STORAGE_KEY_FOR_NAVIGATION_MENU_COLLAPSED, 'true');
    }
  }

  /**
   * Closes the advanced search when the user clicks an app in the navigation, so the selected app is visible. The
   * click is evaluated instead of the itemSelect output of the navigation bar, because the navigation bar does not
   * report a selection for the app that is already active.
   */
  @HostListener('document:click', ['$event'])
  onNavigationClick(event: MouseEvent): void {
    const clickedAppLink: Element | null = (event.target as Element)?.closest('ds-navigation-bar-item a[href]');
    if (clickedAppLink) {
      this.vehicleSearchService.closeAdvancedSearch();
    }
  }

  private createAppEntries(): void {
    // navigation menu should be recreated after update, otherwise the initial app will not be highlighted
    this.showSidebar = false;
    this.navigationItems = [];
    for (const navigationItem of environment.navigationItems) {
      if (this.isNavigationCategory(navigationItem)) {
        this.addCategoryToNavigation(navigationItem);
      } else {
        this.addTopLevelAppToNavigation(navigationItem);
      }
    }
    this.showSidebar = true;
  }

  private addTopLevelAppToNavigation(navigationItem: IIframeAppConfiguration | IWebComponentAppConfiguration) {
    if (this.appsService.appVisibleForUser(navigationItem.routingPath)) {
      this.navigationItems.push({
        label: this.translate.instant(navigationItem.titleResource),
        routerLink: navigationItem.routingPath,
        icon: navigationItem.icon
      });
    }
  }

  private addCategoryToNavigation(navigationItem: INavigationCategory) {
    const category = {
      label: this.translate.instant(navigationItem.titleResource),
      group: true,
      children: []
    };
    navigationItem.apps.forEach((app) => {
      if (this.appsService.appVisibleForUser(app.routingPath)) {
        category.children.push({
          label: this.translate.instant(app.titleResource),
          routerLink: app.routingPath,
          icon: app.icon
        });
      }
    });
    if (category.children.length > 0) {
      this.navigationItems.push(category);
    }
  }

  private isNavigationCategory(item: any): item is INavigationCategory {
    return item.hasOwnProperty('apps');
  }
}
